import React from "react";
import PropTypes from "prop-types";
import Skeleton from "react-loading-skeleton";
import PortalDropdown, { DropdownMenuItem } from "views_shared/app/javascript/components/PortalDropdown";
import ListRow from "../../../profiles/ListRow";

const AVATAR_SIZE = "45px";

// The placeholder for one row, and only one row: the list owns the <ul>, the <li>s and the divider
// between them, so a placeholder that emitted its own list item would land outside any list at the
// infinite-scroll trigger. Mirrors ListRow's geometry so the rows do not shift as they land.
function ListItemSkeleton() {
  return (
    <div className="d-flex align-items-center">
      <div className="flex-shrink-0 me-2">
        <Skeleton circle inline height={AVATAR_SIZE} width={AVATAR_SIZE} />
      </div>
      <div className="flex-grow-1 overflow-hidden">
        <Skeleton width="60%" />
        <Skeleton width="35%" />
      </div>
    </div>
  );
}

function RemoveRepresentativeMenu({ toggleAriaLabel, onRemove }) {
  return (
    <PortalDropdown
      toggleAriaLabel={toggleAriaLabel}
      toggleIcon="fa-solid fa-ellipsis-vertical"
      toggleClassName="btn btn-link text-primary-60 p-0"
    >
      <DropdownMenuItem icon="fa-solid fa-trash" label="Remove representative" onClick={onRemove} />
    </PortalDropdown>
  );
}

RemoveRepresentativeMenu.propTypes = {
  toggleAriaLabel: PropTypes.string.isRequired,
  onRemove: PropTypes.func.isRequired,
};

function ListItem({ profile, onRemove, isEditable = false }) {
  if (!profile) {
    return <ListItemSkeleton />;
  }

  return (
    <ListRow
      imageUrl={profile.profile_image_url}
      href={`/profiles/${profile.slug}`}
      title={profile.name}
      subtitleCompanyName={profile.workplace_name}
      actions={
        isEditable ? (
          // The toggle opens a menu; naming it after the menu's one item would tell a screen reader
          // this control destroys the representation it only offers a choice about — and would
          // collide with the confirmation's own "Remove" button.
          <RemoveRepresentativeMenu
            toggleAriaLabel={`More actions for ${profile.name}`}
            onRemove={() => onRemove({ id: profile.representation_id, name: profile.name })}
          />
        ) : null
      }
    />
  );
}

// The raw representative aggregate from the product view is not RLS-filtered and can also carry
// profiles pr_profiles never serves (a self-expert promoter is served as an expert, not a pr_profile),
// so some representations never hydrate into a profile. An editor still owns those representations and
// has to be able to remove them, so we render this identity-free stub in their place: a neutral avatar,
// a cause-neutral label, and the remove control — no name, no link, nothing about WHY it is unavailable.
function StubListItem({ representationId, onRemove, ordinal = 1, total = 1 }) {
  // Every stub shows the same identity-free label, so once there is more than one the menu toggle needs
  // a positional handle for a screen reader to tell one stub's control (and the confirmation it opens)
  // apart from the next.
  const toggleAriaLabel =
    total > 1
      ? `More actions for unavailable representative ${ordinal} of ${total}`
      : "More actions for this representative";

  return (
    <ListRow
      // No name to derive initials from, so this row opts out of the avatar entirely rather than
      // showing an identity it does not have.
      leading={
        <span
          className="d-inline-flex align-items-center justify-content-center rounded-circle bg-light text-secondary"
          style={{ height: AVATAR_SIZE, width: AVATAR_SIZE }}
        >
          <i className="fa-solid fa-user" />
        </span>
      }
      title="Representative (profile unavailable)"
      actions={
        <RemoveRepresentativeMenu
          toggleAriaLabel={toggleAriaLabel}
          onRemove={() => onRemove({ id: representationId })}
        />
      }
    />
  );
}

StubListItem.propTypes = {
  representationId: PropTypes.number.isRequired,
  onRemove: PropTypes.func.isRequired,
  ordinal: PropTypes.number,
  total: PropTypes.number,
};

ListItem.propTypes = {
  profile: PropTypes.shape({
    profile_image_url: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    representation_id: PropTypes.number.isRequired,
    slug: PropTypes.string.isRequired,
    workplace_name: PropTypes.string,
  }),
  onRemove: PropTypes.func.isRequired,
  isEditable: PropTypes.bool,
};

export { ListItemSkeleton, StubListItem };
export default ListItem;
